import { GameConfig } from "@/config/GameConfig";
import type { LevelDefinition, LevelObjective, PortalDefinition, SurvivalPressureProfile } from "@/levels/types";
import { buildInitialSnakeBody, buildPortalLookup, cellKey, pointsEqual, portalEndpoints, resolvePortalTraversal, stepInDirection } from "@/levels/geometry";
import type { PortalResolution } from "@/levels/geometry";
import { findStarfruitCell, spawnBites } from "./biteSpawn";
import { computeScoreHit } from "./scoreHit";
import { resolveChaosOutcome, type ChaosRulesVersion } from "./chaosOutcome";
import type { RandomSource } from "./rng";
import type { Bite, BiteFeedbackEvent, BiteKind, ChaosOutcomeKind, CompletionReason, Direction, Point } from "./types";
import { ManeuverTracker } from "./scoring/ManeuverTracker";
import { TrickBiteTracker } from "./scoring/TrickBiteTracker";
import { computeManeuverSpeedWeight } from "./scoring/maneuverSpeedWeight";
import { computeLastSecondTurnSpeedMultiplier } from "./scoring/lastSecondTurnSpeedMultiplier";
import type { BiteBonusResult, BiteBonusSource, ManeuverType, SkillMoment } from "./scoring/types";
import {
  SpeedEngine,
  CAMPAIGN_SPEED_CAPS,
  DAILY_SPEED_CAPS,
  ENDLESS_SPEED_CAPS,
  DAILY_V6_SPEED_CAPS,
  SPEED_TRANSITION_MS,
  SPEED_TRIAL_FIXED_STEP_MS,
  computeGrowthBoost,
  computeRushDebtBoost,
  computePressureAdjustedBaseMs,
  computeUncappedTargetStepMs,
  computeV6EffectiveStepMs,
  applySpeedCap,
  computeEffectiveBoostPercent,
  resolveEffectiveTargetStepMs,
  type SpeedCaps,
  type RushDebtTiers,
} from "./SpeedEngine";

/**
 * PR11 (Speed Engine V2) QA/debug diagnostics - every field the QA
 * `?qa=speed` overlay needs to verify the composed movement speed at a
 * glance. Never read by any gameplay/scoring logic; computed fresh on
 * every `getSnapshot()` call, cheap enough to not bother caching.
 */
export interface SpeedDebugSnapshot {
  mode: "campaign" | "daily" | "endless" | "daily-v6";
  /** The level/world's own configured `baseStepMs`, before any pressure adjustment. */
  worldBaseStepMs: number;
  /** `baseStepMs` after Daily's pressure ramp (Campaign: always equal to `worldBaseStepMs`). */
  pressureAdjustedBaseMs: number;
  actualLength: number;
  addedLength: number;
  /** Whole-number percent (1 decimal), e.g. `17.0` for 17%. */
  growthBoostPercent: number;
  cumulativeYellowCount: number;
  rushDebtPercent: number;
  /** The scoring combo (1-5), NOT cumulative yellow count - see `LevelGameSnapshot.rushStack`'s own doc. */
  rushComboStack: number;
  persistentModeBoostPercent: number;
  /** Always 0 in practice as of PR11.1 - SPEED TRIAL no longer contributes a proportional boost here. Reserved for a future genuinely-proportional temporary modifier; see `SpeedCompositionInput.temporarySpeedBoost`'s own doc. */
  temporaryBoostPercent: number;
  /** The absolute minimum step interval currently in force (normal or temporary cap, whichever applies right now). */
  activeCapMs: number;
  /** The raw composition BEFORE any cap - growth + RUSH debt + persistent mode boost applied to the pressure-adjusted base. */
  uncappedTargetStepMs: number;
  /**
   * The UNDERLYING target: `uncappedTargetStepMs` capped by the NORMAL
   * cap - what the run's real, permanent, accumulated state adds up to
   * right now, independent of SPEED TRIAL. This is exactly what SPEED
   * TRIAL returns to when it ends.
   */
  underlyingTargetStepMs: number;
  /** True while SPEED TRIAL's fixed override is in force. */
  isFixedOverrideActive: boolean;
  /** The CONFIGURED fixed override value (75ms) - always shown, whether or not it's currently active, so QA can see the exact number SPEED TRIAL targets. */
  fixedOverrideStepMs: number;
  /** The FINAL effective target the Speed Engine is actually transitioning toward right now: `fixedOverrideStepMs` while `isFixedOverrideActive`, otherwise `underlyingTargetStepMs`. */
  effectiveTargetStepMs: number;
  /** The interval movement ticks are ACTUALLY using this instant - smoothly approaching `effectiveTargetStepMs`. */
  currentStepMs: number;
  currentCellsPerSecond: number;
  isTransitioning: boolean;
  transitionRemainingMs: number;
  speedTrialRemainingMs: number | null;
  /** PR26 CHANGE 4: the Campaign-only post-95ms RUSH speed continuation's current value, or `null` if not yet engaged / not applicable (any non-Campaign mode). See `LevelGame.advancePostCapRushSpeed()`'s own doc. */
  postCapRushStepMs: number | null;
}

export type LevelTickResult =
  | { type: "moved" }
  | { type: "ate"; biteKind: BiteKind; length: number; event: BiteFeedbackEvent }
  | { type: "gate-opened"; biteKind: BiteKind; length: number; event: BiteFeedbackEvent }
  /** The head just entered the open gate; the automatic exit walk has begun. */
  | { type: "exiting"; length: number }
  /** BLACK HOLE V1 (Saturn World): `"black_hole"` is its own distinct reason - a lethal authored tile, never folded into `"obstacle"` at the engine level (see `LevelGame.isBlackHole()`/`LevelDefinition.blackHoles`). `GameScene` maps it to `"obstacle"` only at the leaderboard/analytics submission boundary, so the backend's own `death_reason` check constraint never needs to change. */
  | { type: "game-over"; reason: "wall" | "obstacle" | "self" | "black_hole" }
  | { type: "level-complete" }
  /**
   * Endless Growth V1 only (`level.completeWhenNoBitesCanSpawn`) - a
   * normal post-consumption/post-respawn spawn attempt found zero valid
   * fruit cells left on the fixed arena. Deliberately its own variant,
   * never `"game-over"` or `"level-complete"` - see `completeArenaCleared()`.
   */
  | { type: "arena-cleared" };

export type LevelPhase = "playing" | "exiting" | "game-over" | "level-complete" | "arena-cleared";

/** Everything `advanceRealTime()` can report besides a plain `BiteFeedbackEvent` (a completed SPEED TRIAL) - the temporary STARFRUIT gate closing on its own, or (Endless only) the arena being fully cleared. */
export type LevelRealTimeEvent = BiteFeedbackEvent | { kind: "starfruit-gate-expired" } | { kind: "arena-cleared" };

/** Read-only snapshot of the level's game state, safe to hand to rendering code. */
export interface LevelGameSnapshot {
  snake: readonly Point[];
  /** Normally exactly 3 (one SAFE, one RUSH, one CHAOS) - exactly 4 when an eligible STARFRUIT reveals alongside them. Empty once the gate opens, mid-CHAOS-SPEED-TRIAL, or between eating and the ~250ms respawn. */
  bites: readonly Bite[];
  direction: Direction;
  length: number;
  /**
   * The snake's actual logical length the instant a gate first opened -
   * `null` until then. Fixed for the rest of the level, including all the
   * way through the automatic exit walk, when `length` itself counts down
   * to 0 as segments visually leave the board. This, never `length`, is
   * what actually earned completion and what any run submission/analytics
   * must report as the completed run's final length - see
   * `checkGateOpen()`/`openTemporaryStarfruitGate()`. Reverts to `null` if
   * a temporary STARFRUIT gate closes again without the real target
   * length being reached - the level isn't complete yet, so nothing has
   * actually been "finalized".
   */
  finalLength: number | null;
  targetLength: number;
  score: number;
  /**
   * The RUSH SCORING COMBO, capped at 5 - drives the score table and the
   * displayed multiplier ("x5" is the cap, even mid-combo). Reset by
   * SAFE/CHAOS/STARFRUIT. Deliberately NOT the same thing as permanent
   * RUSH speed debt (`cumulativeYellowCount`), which is never reset by
   * any of those - see PR11 (Speed Engine V2)'s combo-vs-debt split.
   */
  rushStack: number;
  /** The scoring combo's own UNCAPPED counter - resets together with `rushStack`, drives only the "MAX" display badge once it climbs past 5. Always >= `rushStack`. Still not RUSH debt - see `cumulativeYellowCount`. */
  rushComboStreak: number;
  /** Cumulative yellow (RUSH) fruit eaten this run - never reset by SAFE/CHAOS/STARFRUIT/gate-opening, only by a fresh run. Drives permanent RUSH speed debt (see `SpeedEngine`/`speedDebug`); never itself the scoring combo. */
  cumulativeYellowCount: number;
  /** The TOTAL composed speed boost currently in effect (growth + RUSH debt + persistent mode + temporary), as a whole-number percent, already cap-aware (never overstates what the player actually feels). */
  speedBoostPercent: number;
  /** True while either a permanent (real target length reached) or temporary (STARFRUIT) gate is open. */
  isGateOpen: boolean;
  /** Why the gate is (or was) open - `null` until it first opens. See `CompletionReason`. */
  completionReason: CompletionReason | null;
  phase: LevelPhase;
  isGameOver: boolean;
  isLevelComplete: boolean;
  /** Endless Growth V1 only - true once `phase === "arena-cleared"`. See `LevelTickResult`'s `"arena-cleared"` variant. */
  isArenaCleared: boolean;
  /** The interval movement ticks actually use right now - the Speed Engine's current INTERPOLATED value (smoothly approaching `targetStepMs`), or the separate gate-exit animation's own interval while `phase === "exiting"`. */
  stepMs: number;
  /** What `stepMs` is smoothly transitioning toward - equal to `stepMs` whenever no transition is in flight. `exitStepMs` while exiting (the exit walk has no transition concept of its own). */
  targetStepMs: number;
  isSpeedTransitioning: boolean;
  /** Milliseconds of active time left in the in-flight speed transition, 0 when none is active. */
  speedTransitionRemainingMs: number;
  /** PR11 (Speed Engine V2) QA/debug diagnostics - see `SpeedDebugSnapshot`'s own doc. Never used by gameplay/scoring, only the `?qa=speed` overlay. */
  speedDebug: SpeedDebugSnapshot;
  speedTrialRemainingMs: number | null;
  /** Milliseconds left in an active WORMHOLE effect, or `null` when inactive. Only ever advanced by `advanceRealTime()`, so it's naturally frozen whenever the caller stops calling it (Pause). */
  wormholeRemainingMs: number | null;
  /** Milliseconds left in the temporary STARFRUIT gate window, or `null` when not currently open via STARFRUIT (never opened, expired, or promoted to permanent). */
  starfruitGateRemainingMs: number | null;
  /** True while a fresh bite group is scheduled to appear (the ~250ms respawn delay) but hasn't yet - part of the no-soft-lock invariant, see `noSoftLockInvariantHolds`. */
  pendingRespawn: boolean;
  /** PR26 CHANGE 8: true once the 250ms respawn delay has already elapsed but the last spawn attempt found zero legal/reachable cells - retried after each subsequent safe executed move, never abandoned, never a fresh 250ms wait. Also part of the no-soft-lock invariant - see `noSoftLockInvariantHolds`. */
  awaitingSpawnRetry: boolean;

  // -- Scoring V2: Skill Score. See `ManeuverTracker` for the rules behind every one of these. --
  /** Executed movement steps so far this run - invalid/rejected direction inputs never reach `tick()`, so they're never counted; the automatic gate-exit walk doesn't call `tick()` with a direction either, so it's never counted. */
  moveCount: number;
  /** Sum of every credited skill moment's `awardedPoints` this run. */
  skillScore: number;
  /** `score + skillScore` - the "Final Score so far" the main HUD shows during play; Pace Bonus is only ever added once, at completion. */
  finalScoreSoFar: number;
  /** Credited-occurrence count per maneuver type (includes exhausted 4th+ occurrences, which still close an anchor - excludes duplicate-anchor detections, which are never counted at all). */
  maneuverCounts: Record<ManeuverType, number>;
  doubleDangerCount: number;
  bestFlowStreak: number;
  currentFlowStreak: number;
  currentFlowMultiplier: number;
  /** PR26 LAST-SECOND TURN V4: whether a LAST-SECOND TURN detected right now would actually be credited - `true` at the start of a fresh run. See `ManeuverTracker`'s own re-arm doc. */
  lastSecondTurnArmed: boolean;
  /** PR26 LAST-SECOND TURN V4: consecutive safe-forward ticks accumulated so far while disarmed (0-`reArmSafeMoves`, exclusive of the tick that re-arms) - always 0 while armed. */
  lastSecondTurnReArmProgress: number;

  // -- Scoring V2: TRICK BITE / CLUTCH BITE. See `TrickBiteTracker`. --
  trickBiteCount: number;
  clutchBiteCount: number;
  /** How many more executed movement steps the TRICK BITE window stays open - 0 means inactive. Decrements once per safe executed movement step; a fresh credited maneuver resets it back to `GameConfig.scoring.biteBonus.windowMoves`. */
  trickBiteWindowRemainingMoves: number;
  /** The maneuver type(s) that opened/most-recently-refreshed the currently active window - `null` whenever the window is inactive OR its currently-authoritative source is WORMHOLE. */
  trickBiteWindowOpenedBy: readonly ManeuverType[] | null;
  /** PR13-B: which opener source is currently authoritative for the active window (`"maneuver"` or `"wormhole"`) - `null` whenever the window is inactive. Drives the "WORMHOLE BITE READY: N" vs. generic "TRICK BITE READY: N" indicator. */
  trickBiteWindowSource: BiteBonusSource | null;
}

/**
 * The core liveness guarantee for active play: whenever the level isn't
 * over/won and the gate is still locked, the player must always have
 * *something* that can grow the snake - either a live bite group on the
 * board, a scheduled respawn about to produce one, a SPEED TRIAL in
 * progress (which itself always ends in either a fresh group or an open
 * gate), or (PR26 CHANGE 8) a pending zero-fruit respawn actively retrying
 * on every subsequent move. If none of these hold, the game has
 * soft-locked - eating is the only way to progress, and there's nothing
 * left to eat. Before CHANGE 8, a Campaign level whose respawn delay
 * expired with zero legal spawn cells left `pendingRespawn` (and every
 * other case here) `false` forever - this invariant would have caught
 * that exact bug; `awaitingSpawnRetry` is what keeps it holding `true`
 * for that state now, since the game genuinely still has a path forward.
 */
export function noSoftLockInvariantHolds(snapshot: LevelGameSnapshot): boolean {
  if (snapshot.phase !== "playing") return true;
  if (snapshot.isGateOpen) return true;
  if (snapshot.speedTrialRemainingMs !== null) return true;
  // >= 3, not === 3: an eligible STARFRUIT reveal makes this 4.
  if (snapshot.bites.length >= 3) return true;
  return snapshot.pendingRespawn || snapshot.awaitingSpawnRetry;
}

/**
 * Pure, framework-agnostic rules engine for a single campaign level,
 * including the "CHOOSE YOUR BITE" food system (SAFE / RUSH / CHAOS),
 * the legendary STARFRUIT fourth bite, the WORMHOLE CHAOS outcome,
 * scoring, the RUSH speed/multiplier streak, the automatic gate-exit
 * walk, Portals V1's permanent authored map portals (see
 * `LevelDefinition.portals`/`PortalPair`/`PortalNetwork`) - deliberately a
 * separate system from WORMHOLE: fixed/bidirectional/direction-preserving
 * (a `PortalPair`) or random-destination (a `PortalNetwork`, JUNCTION
 * gameplay-QA pass) map geometry, never a temporary random CHAOS effect -
 * and BLACK HOLE V1's
 * permanent authored lethal tiles (see `LevelDefinition.blackHoles`),
 * its own distinct `"black_hole"` game-over reason rather than an
 * ordinary obstacle death. Deliberately has no knowledge of Phaser, the
 * DOM, or rendering - game rules, level data, progress storage and
 * Phaser rendering are all independent layers.
 */
export class LevelGame {
  private readonly cols: number;
  private readonly rows: number;
  private readonly obstacleKeys: ReadonlySet<string>;
  /** Portals V1 (Saturn World) - see `PortalPair`'s own doc. `[]`/empty for every level without `portals`, so this is a no-op for Moon/Earth/Mars/Daily/Endless. Built once at construction, not per tick. */
  private readonly portalLookup: ReadonlyMap<string, PortalResolution>;
  /** Every portal endpoint cell key, for the bite-spawn blocklist only - a fruit must never spawn on a cell that also teleports the player. Independent of `portalLookup`'s resolution direction. */
  private readonly portalEndpointKeys: ReadonlySet<string>;
  /** BLACK HOLE V1 (Saturn World) - see `LevelDefinition.blackHoles`'s doc. `[]`/empty for every level without `blackHoles`, so this is a no-op for every other level. */
  private readonly blackHoleKeys: ReadonlySet<string>;
  private readonly exit: Point;
  private readonly targetLength: number;
  private readonly baseStepMs: number;
  /** `"campaign"` (the default) or `"survival"` - see `LevelObjective`'s doc. Read once at construction, same as every other `level.*` field. */
  private readonly objective: LevelObjective;
  private readonly pressure: SurvivalPressureProfile | undefined;
  /** Daily V6 only (PR25) - see `LevelDefinition.pressureCurve`'s own doc. `undefined` for every V1-V5 Daily spec and every Campaign/Endless level, so `currentUnderlyingTargetStepMs()`'s V6 branch never fires for them. */
  private readonly pressureCurve: LevelDefinition["pressureCurve"];
  /** Daily V6 only (PR25, section 8) - see `LevelDefinition.initialFruitExclusionCells`'s own doc. `[]`/empty for every level that doesn't set it, so `spawnBitesNow()`'s initial-only exclusion is a no-op everywhere else. */
  private readonly initialFruitExclusionKeys: ReadonlySet<string>;
  /** Endless Growth V1 only - see `LevelDefinition.completeWhenNoBitesCanSpawn`'s own doc. `false` for every Campaign level and every Daily survival level, so their behavior is byte-for-byte unaffected. */
  private readonly completeWhenNoBitesCanSpawn: boolean;
  /** Active (pause-excluded) elapsed time this run, in ms - only ever advanced by `advanceRealTime()`, so pausing/backgrounding the tab freezes it exactly like every other real-time countdown in this engine. Drives the survival pressure ramp; unused (and irrelevant) for a campaign level. */
  private activeElapsedMs = 0;
  /** Drives bite placement (which cells a bite group lands on). */
  private readonly random: RandomSource;
  /** Drives the CHAOS outcome roll only - separately injectable so tests (and a future Daily Challenge) can force/reproduce an exact CHAOS result without also having to predict bite placement. Defaults to the same source as `random`. */
  private readonly chaosRandom: RandomSource;
  /** Drives the once-per-run STARFRUIT eligibility roll only - separately injectable for the same reason as `chaosRandom`, and so a future Daily Challenge can fix eligibility independently of bite placement/CHAOS. Defaults to the same source as `random`. */
  private readonly starfruitRandom: RandomSource;
  /** JUNCTION gameplay-QA pass: drives a `PortalNetwork`'s random-destination pick only - same "separately injectable, defaults to `random`" pattern as `chaosRandom`/`starfruitRandom`, so a test can force/prove a specific destination without also having to predict bite placement. A no-op for every level with no `PortalNetwork` (every fixed `PortalPair`, and every level with no `portals` at all). */
  private readonly portalRandom: RandomSource;
  /** Which bite kinds a fruit group may spawn as - defaults to the full SAFE/RUSH/CHAOS trio (every campaign level, unchanged). Daily Challenge's `safe_only`/`rush_only`/`chaos_only` bite modes restrict this to a single kind - see `src/daily/types.ts`'s `DailyBiteMode` and `spawnBites()`'s `buildKindsForSpawn()`. */
  private readonly allowedBiteKinds: readonly BiteKind[];

  private snakeBody: Point[] = [];
  private snakeKeys: Set<string> = new Set();
  private currentDirection: Direction;
  private bites: Bite[] = [];
  /** The real, permanent gate - opened once the snake's actual logical length reaches `targetLength`, never closes again. */
  private gatePermanentlyOpen = false;
  /** The temporary STARFRUIT gate - opened for exactly `GameConfig.starfruit.gateDurationMs` of active time, closes again unless the real target length is reached first (see `checkGateOpen`'s promotion in `advanceRealTime`). */
  private starfruitGate: { remainingMs: number } | null = null;
  private finalLength: number | null = null;
  private completionReason: CompletionReason | null = null;
  private phase: LevelPhase = "playing";
  private gameOverReason: "wall" | "obstacle" | "self" | "black_hole" = "wall";

  private score = 0;
  /** The RUSH SCORING COMBO (capped 1-5) - see `LevelGameSnapshot.rushStack`'s doc. Reset by SAFE/CHAOS/STARFRUIT via `resetCombo()`. */
  private rushComboStack = 0;
  /** The scoring combo's own uncapped counter - resets together with `rushComboStack`, drives only the "MAX" display badge. */
  private rushComboUncapped = 0;
  /** Cumulative yellow (RUSH) fruit eaten this run - drives permanent RUSH speed debt. Never reset by `resetCombo()`/SAFE/CHAOS/STARFRUIT/gate-opening - only a fresh `reset()` zeroes it. See `LevelGameSnapshot.cumulativeYellowCount`'s doc. */
  private cumulativeYellowCount = 0;
  /**
   * PR26 CHANGE 4: Campaign-only post-95ms RUSH speed continuation state.
   * `null` until the ordinary composed (uncapped) target first drops to
   * <=95ms on a RUSH pick - from that pick on, this is the authoritative
   * Campaign target step interval (see `applyCampaignAwareSpeedCap()`),
   * updated ONLY by `advancePostCapRushSpeed()`, which is called ONLY for
   * a RUSH bite (never SAFE/CHAOS/STARFRUIT/SPEED-TRIAL-completion - see
   * that method's own doc for why those must never touch it). Always
   * `null` for every non-Campaign mode (Daily/Endless keep their own
   * existing cap logic entirely untouched). Only a fresh `reset()` zeroes
   * it, same lifecycle as `cumulativeYellowCount`.
   */
  private postCapRushStepMs: number | null = null;
  private speedTrial: { remainingMs: number } | null = null;
  private wormhole: { remainingMs: number } | null = null;
  private respawnTimerMs = 0;
  /**
   * PR26 CHANGE 8 (Campaign zero-fruit soft-lock fix): `true` once the
   * normal 250ms respawn delay has already elapsed but `spawnBitesNow()`
   * found zero legal/reachable cells - `this.bites` stays `[]` and NO
   * fresh 250ms wait starts (`respawnTimerMs` stays at 0). Retried once
   * per subsequent safe executed movement tick (`retryPendingRespawnIfNeeded()`,
   * called from `tick()`'s plain-move branch - board occupancy can only
   * change via an actual move, never via the mere passage of real time),
   * using the exact same `spawnBitesNow()` and its unmodified safety
   * invariants. Never used by Endless (`completeWhenNoBitesCanSpawn`
   * levels take the existing `arena-cleared` path instead, unchanged).
   * Only a fresh `reset()` (or gate resolution/completion, defensively)
   * clears it.
   */
  private awaitingSpawnRetry = false;
  private exitStepMs = 0;
  /**
   * PR11 (Speed Engine V2) - the single authority for this run's
   * player-controlled movement interval. Constructed fresh in every
   * `reset()` call (same "fresh instance per run" pattern as
   * `maneuverTracker`/`trickBiteTracker`), snapped directly to this run's
   * own starting target with no transition ("no carry-over from a
   * previous run"). Deliberately never touched by `checkGateOpen()`/
   * `openTemporaryStarfruitGate()` - opening the gate must never alter
   * speed state.
   */
  private speedEngine: SpeedEngine = new SpeedEngine(0);
  /** `"campaign"`, `"daily"`, `"daily-v6"`, or `"endless"` - which `SpeedCaps` this run is bound by. Read once at construction from `level.speedMode` (defaults `"campaign"`). Deliberately independent of `objective` - a legacy V1 Daily spec is `objective: "campaign"` but must still use Daily's caps, and Endless (`objective: "survival"`) must still use its own permanent cap. */
  private readonly speedMode: "campaign" | "daily" | "endless" | "daily-v6";
  private readonly speedCaps: SpeedCaps;
  /** A flat, persistent percentage-point boost present from the run's first movement step, never reset mid-run - Daily's `high_speed` modifier (V3+) is the only current source. 0 for every Campaign level and every non-`high_speed` Daily day. */
  private readonly persistentModeBoost: number;
  /** PR13-A: which RUSH debt tier table (`SpeedEngine.RushDebtTiers`) this run composes with - read once at construction from `level.rushDebtProfile` (defaults to the CURRENT table). See that field's own doc. */
  private readonly rushDebtTiers: RushDebtTiers;
  /** PR13-A: which CHAOS outcome weight table (`chaosOutcome.ChaosRulesVersion`) this run rolls from - read once at construction from `level.chaosRulesVersion` (defaults to `"current"`). See that field's own doc. */
  private readonly chaosRulesVersion: ChaosRulesVersion;

  /** Counts every bite group actually created this run (the first, at `reset()`, is group 1) - STARFRUIT can only ever reveal on `GameConfig.starfruit.revealGroupNumber`. */
  private fruitGroupCount = 0;
  /** Rolled exactly once per run (see `reset()`) - never re-rolled per group. Deliberately never exposed on the snapshot; the result must stay hidden from the player. */
  private starfruitEligible = false;
  private starfruitSpawnedThisRun = false;

  /**
   * Deterministic QA gameplay controls (audio-integration follow-up):
   * `?qa=chaos`-only, gated at construction by `qaOverridesEnabled` (which
   * `GameScene` only ever sets `true` after its own `isQaModeAllowedHost`
   * check - never reachable on `snakescore.com`). Both setters below are
   * unconditional no-ops when this is `false`, so calling them from
   * anywhere else (a stray devtools call included) can never affect real
   * probabilities/RNG. Neither ever changes `chaosRandom`/`starfruitRandom`
   * itself - they short-circuit the very next resolution instead, so a
   * cleared override always leaves normal randomness exactly as it was.
   */
  private readonly qaOverridesEnabled: boolean;
  /** One-shot: consumed (reset to `null`) the instant the next CHAOS bite actually resolves, whether or not this override was set. */
  private qaForcedChaosOutcome: ChaosOutcomeKind | null = null;
  /** One-shot: consumed (reset to `false`) the instant a bite group is actually spawned with a forced STARFRUIT included - bypasses the normal once-per-run-at-group-2 constraint entirely, since it exists to let QA reach STARFRUIT on demand at any point in a real run. */
  private qaForceStarfruitNextSpawn = false;

  // -- Scoring V2 --
  private moveCount = 0;
  private maneuverTracker = new ManeuverTracker();
  private trickBiteTracker = new TrickBiteTracker();
  /** The skill moment (if any) resolved by the most recent `tick()` call - read via `getLastSkillMoment()` right after `tick()` returns, the same pattern rendering code already uses for wormhole-wrap detection. Reset to `null` at the top of every `tick()`. */
  private lastSkillMoment: SkillMoment | null = null;
  /** The TRICK/CLUTCH BITE (if any) awarded by the most recent `tick()` call - same read-immediately-after-`tick()` pattern as `lastSkillMoment`. Reset to `null` at the top of every `tick()`. */
  private lastBiteBonus: BiteBonusResult | null = null;
  /**
   * PR13-C: whether the most recent `tick()` call committed a successful
   * WORMHOLE boundary wrap - the same authoritative value already computed
   * for `recordManeuvers()`'s `wormholeWrapSuccess` input, just also kept
   * here (same read-immediately-after-`tick()` pattern as
   * `lastSkillMoment`/`lastBiteBonus`) so rendering code has a real engine
   * signal instead of inferring a wrap from position deltas between two
   * snapshots. Reset to `false` at the top of every `tick()`. Observational
   * only - never read by any gameplay/scoring logic.
   */
  private lastWormholeWrapSuccess = false;
  /**
   * Portals V1 (Saturn World): the portal traversal (if any) actually
   * committed by the most recent `tick()` call - same read-immediately-
   * after-`tick()` pattern as `lastSkillMoment`/`lastWormholeWrapSuccess`.
   * Set only once the resolved head has passed every lethal check and been
   * committed to the body (same ordering rule as `lastWormholeWrapSuccess`)
   * - a teleport that ends in death at the destination never sets this.
   * Observational only - never read by any gameplay/scoring logic.
   */
  private lastPortalTraversal: { pairId: string; entry: Point; exit: Point } | null = null;

  constructor(
    private readonly level: LevelDefinition,
    options: {
      random?: RandomSource;
      chaosRandom?: RandomSource;
      starfruitRandom?: RandomSource;
      /** JUNCTION gameplay-QA pass - see `portalRandom`'s own field doc. Defaults to the same source as `random`. */
      portalRandom?: RandomSource;
      allowedBiteKinds?: readonly BiteKind[];
      /** `?qa=chaos`-only deterministic gameplay overrides - see the field's own doc above. Defaults to `false` for every real Campaign/Daily run. */
      qaOverridesEnabled?: boolean;
      /**
       * PR34: the gate target THIS run must reach, when it differs from the
       * level's own raw `targetLength` - only ever the Mobile Mars/Saturn/Sun
       * Campaign target from `getEffectiveTargetLength()`. Replaces the one
       * number the length-based gate (and STARFRUIT's gate promotion) compares
       * against, and is what `getSnapshot().targetLength` reports; nothing
       * else in the engine reads it. `level` itself is never mutated.
       */
      targetLengthOverride?: number;
    } = {},
  ) {
    this.qaOverridesEnabled = options.qaOverridesEnabled ?? false;
    this.cols = level.grid.cols;
    this.rows = level.grid.rows;
    this.obstacleKeys = new Set(level.obstacles.map(cellKey));
    this.portalLookup = buildPortalLookup(level.portals);
    this.portalEndpointKeys = new Set(portalEndpoints(level.portals).map(cellKey));
    this.blackHoleKeys = new Set((level.blackHoles ?? []).map(cellKey));
    this.exit = level.exit;
    this.targetLength = options.targetLengthOverride ?? level.targetLength;
    this.baseStepMs = level.baseStepMs;
    this.objective = level.objective ?? "campaign";
    this.pressure = level.pressure;
    this.pressureCurve = level.pressureCurve;
    this.initialFruitExclusionKeys = new Set((level.initialFruitExclusionCells ?? []).map(cellKey));
    this.completeWhenNoBitesCanSpawn = level.completeWhenNoBitesCanSpawn ?? false;
    this.speedMode = level.speedMode ?? "campaign";
    this.speedCaps =
      this.speedMode === "daily" ? DAILY_SPEED_CAPS : this.speedMode === "daily-v6" ? DAILY_V6_SPEED_CAPS : this.speedMode === "endless" ? ENDLESS_SPEED_CAPS : CAMPAIGN_SPEED_CAPS;
    this.persistentModeBoost = level.persistentModeBoost ?? 0;
    this.rushDebtTiers = level.rushDebtProfile === "legacy-v1" ? GameConfig.speedEngine.rushDebtLegacyV1 : GameConfig.speedEngine.rushDebt;
    this.chaosRulesVersion = level.chaosRulesVersion === "legacy-v1" ? "legacy-v1" : "current";
    this.currentDirection = level.startDirection;
    this.random = options.random ?? Math.random;
    this.chaosRandom = options.chaosRandom ?? this.random;
    this.starfruitRandom = options.starfruitRandom ?? this.random;
    this.portalRandom = options.portalRandom ?? this.random;
    this.allowedBiteKinds = options.allowedBiteKinds ?? ["safe", "rush", "chaos"];
    this.reset();
  }

  /**
   * QA-only: forces the very next CHAOS bite's outcome to `outcome`,
   * bypassing `resolveChaosOutcome()`'s normal roll for exactly that one
   * resolution - real probabilities/RNG are never touched (the underlying
   * `chaosRandom` source is neither read nor mutated), and every
   * subsequent CHAOS bite resolves normally again. A no-op unless this
   * instance was constructed with `qaOverridesEnabled: true`.
   */
  setQaForcedChaosOutcome(outcome: ChaosOutcomeKind): void {
    if (!this.qaOverridesEnabled) return;
    this.qaForcedChaosOutcome = outcome;
  }

  /**
   * QA-only: forces STARFRUIT to be included in the very next fruit group
   * actually spawned, regardless of the normal once-per-run eligibility
   * roll or the group-2-only reveal rule - lets a QA session reach
   * STARFRUIT on demand at any point in a real run, rather than waiting
   * for its real ~2%-per-run rarity. One-shot: cleared the instant a
   * group is spawned with it included (never retried if that spawn's
   * bounded placement search happens to fail - same "skip, don't hang or
   * retry forever" rule the real eligible-but-unplaceable case already
   * follows). A no-op unless this instance was constructed with
   * `qaOverridesEnabled: true`.
   */
  setQaForceStarfruitNextSpawn(): void {
    if (!this.qaOverridesEnabled) return;
    this.qaForceStarfruitNextSpawn = true;
  }

  reset(): void {
    this.snakeBody = buildInitialSnakeBody(this.level.snakeStart, this.level.startDirection, this.level.initialLength);
    this.snakeKeys = new Set(this.snakeBody.map(cellKey));
    this.currentDirection = this.level.startDirection;
    this.gatePermanentlyOpen = false;
    this.starfruitGate = null;
    this.finalLength = null;
    this.completionReason = null;
    this.phase = "playing";
    this.score = 0;
    this.rushComboStack = 0;
    this.rushComboUncapped = 0;
    this.cumulativeYellowCount = 0;
    this.postCapRushStepMs = null;
    this.speedTrial = null;
    this.wormhole = null;
    this.respawnTimerMs = 0;
    this.awaitingSpawnRetry = false;
    this.exitStepMs = 0;
    this.activeElapsedMs = 0;
    this.fruitGroupCount = 0;
    // Exactly one eligibility decision for this run - never re-rolled for
    // any later group. A fresh LevelGame (e.g. REPLAY) means a fresh,
    // independent roll.
    this.starfruitEligible = this.starfruitRandom() < GameConfig.starfruit.probability;
    this.starfruitSpawnedThisRun = false;
    this.moveCount = 0;
    this.maneuverTracker = new ManeuverTracker();
    this.trickBiteTracker = new TrickBiteTracker();
    this.lastSkillMoment = null;
    this.lastBiteBonus = null;
    this.lastWormholeWrapSuccess = false;
    this.lastPortalTraversal = null;
    // Every growth/debt/pressure/temporary-boost input above is already
    // zeroed by this point, so this is exactly this run's own starting
    // target - a fresh `SpeedEngine`, snapped to it with zero transition
    // ("no carry-over from a previous run").
    this.speedEngine = new SpeedEngine(this.computeCappedTargetStepMs());
    this.bites = this.spawnBitesNow(true);
  }

  /** Advances the game by exactly one grid step. */
  tick(requestedDirection: Direction): LevelTickResult {
    if (this.phase === "game-over") return { type: "game-over", reason: this.gameOverReason };
    if (this.phase === "level-complete") return { type: "level-complete" };
    if (this.phase === "arena-cleared") return { type: "arena-cleared" };
    if (this.phase === "exiting") return this.exitStep();

    // Every path below this point is a genuine, player-driven executed
    // movement attempt (the automatic exit walk above returns early, via
    // `exitStep()`, without ever reaching here) - including the terminal
    // gate-entry move and a lethal terminal move, both of which still
    // "attempt" a move even though the run is about to end. Invalid/
    // rejected direction inputs never reach `tick()` at all (see
    // `InputQueue`), so they're excluded by construction, not by any
    // check here.
    this.moveCount += 1;
    this.lastSkillMoment = null;
    this.lastBiteBonus = null;
    this.lastWormholeWrapSuccess = false;
    this.lastPortalTraversal = null;

    const prevDirection = this.currentDirection;
    this.currentDirection = requestedDirection;
    const head = this.snakeBody[0];
    const rawNewHead = stepInDirection(head, requestedDirection);
    // Captured before any bite effect this same tick could end WORMHOLE -
    // maneuver detection must judge this move's own hazard geometry by
    // the state that actually governed it, not by what's left afterward.
    const wormholeActiveForThisMove = this.wormhole !== null;
    // PR26 CHANGE 1 (LAST-SECOND TURN V2): must be captured HERE, before
    // any of this tick's own state mutations (head/tail/portal/WORMHOLE),
    // since it answers "would continuing straight from the PRE-tick state
    // have been lethal" - see `wouldContinuingStraightBeLethal()`'s own
    // doc for why this can't be computed later (by the time
    // `recordManeuvers()` runs, `this.snakeKeys` already reflects the
    // turn actually taken this tick, not the hypothetical straight one).
    const continuingStraightWouldBeLethal = this.wouldContinuingStraightBeLethal(head, prevDirection, wormholeActiveForThisMove);

    // Gate priority over WORMHOLE edge-wrapping: entering an open gate
    // positioned at the edge always transitions to EXITING, it never
    // wraps - checked against the raw (unwrapped) coordinate, before wrap
    // is ever computed below.
    if (this.isGateOpenNow() && pointsEqual(rawNewHead, this.exit)) {
      this.snakeBody.unshift(rawNewHead);
      this.snakeKeys.add(cellKey(rawNewHead));
      this.dropTail();
      // Entering EXITING cancels WORMHOLE and the temporary gate timer -
      // completionReason/finalLength were already captured by whichever
      // gate-opening path got us here, and stay untouched.
      this.wormhole = null;
      this.starfruitGate = null;
      this.phase = "exiting";
      this.exitStepMs = computeExitStepMs(this.snakeBody.length);
      // Entering the (already-open) gate is a safe executed movement step
      // - the TRICK BITE window still ticks/refreshes normally - but never
      // itself an eaten-fruit event, so it can never earn a bite bonus.
      this.recordManeuvers({ prevDirection, direction: requestedDirection, headBefore: head, headAfter: rawNewHead, wormholeActiveForThisMove, wormholeWrapSuccess: false, eatenBiteKind: null, continuingStraightWouldBeLethal });
      // PR13-B: gate priority is checked (and returns) BEFORE wrap is ever
      // computed below, so a gate-entry move can never be misclassified as
      // a wrap - and gate entry/EXITING must clear any active Trick/Clutch
      // window (whichever source), per the WORMHOLE scoring window's
      // lifecycle rules. Whatever bonus the fruit that opened THIS gate
      // already earned (on an earlier tick) is untouched - only the
      // forward-looking window clears.
      this.trickBiteTracker.clearWindow();
      return { type: "exiting", length: this.snakeBody.length };
    }

    const wrapped = this.wormhole ? this.wrapIfOutOfBounds(rawNewHead) : null;
    const preportalHead = wrapped ?? rawNewHead;
    // Portals V1 (Saturn World): a single, one-shot lookup against the
    // already-resolved (in-bounds, post-wrap) candidate cell - WORMHOLE's
    // boundary wrap only ever applies to the raw out-of-bounds case above,
    // so the two never compete for the same move. `resolvePortalTraversal`
    // reads `portalLookup` exactly once and never re-resolves its own
    // result, so a portal chain/recursive teleport is structurally
    // impossible regardless of authored level data (see `LevelValidator`
    // for the authoring-time guarantee that no endpoint is itself another
    // pair's endpoint). `null` for every level without `portals` - this
    // branch is then a no-op, and `newHead` below is byte-for-byte what it
    // already was before Portals V1.
    const portalResolution = resolvePortalTraversal(preportalHead, this.portalLookup);
    // JUNCTION gameplay-QA pass (`PortalNetwork`): the actual destination
    // pick only exists once a real traversal is happening - `buildPortalLookup()`
    // stays pure/RNG-free, this is the one place `portalRandom` is ever
    // read. `pickRandomPortalDestination()` always returns one of
    // `candidates` (never the entered endpoint itself - already excluded
    // when the lookup was built), so this can never resolve back to where
    // the head just came from.
    const portalTo = portalResolution
      ? portalResolution.kind === "fixed"
        ? portalResolution.to
        : this.pickRandomPortalDestination(portalResolution.candidates)
      : null;
    const newHead = portalTo ?? preportalHead;

    if (this.hitsWall(newHead)) return this.die("wall");
    if (this.isObstacle(newHead)) return this.die("obstacle");
    // BLACK HOLE V1 (Saturn World): its own distinct reason, checked
    // before self-collision - `blackHoleKeys` is disjoint from both
    // `obstacleKeys` and the snake's own body by `LevelValidator`'s
    // authoring-time guarantee, so ordering can't actually change the
    // outcome, but this keeps "entering a black hole" from ever being
    // reported as an ordinary obstacle/self death. Unlike every other
    // lethal cell (wall/obstacle/self never actually commit the head onto
    // the fatal cell - `snakeBody` simply stops one cell short), a black
    // hole DOES commit the head onto it first (`unshift` + `dropTail`,
    // net zero length change, so the submitted `finalLength` is identical
    // either way) - the whole point of the consumption animation is that
    // the player visibly sees the head reach the hazard before it's
    // swallowed, not stop short of it.
    if (this.isBlackHole(newHead)) {
      this.snakeBody.unshift(newHead);
      this.snakeKeys.add(cellKey(newHead));
      this.dropTail();
      return this.die("black_hole");
    }
    if (this.hitsBody(newHead)) return this.die("self");

    this.snakeBody.unshift(newHead);
    this.snakeKeys.add(cellKey(newHead));
    // PR13-B: the ONLY authoritative "successful WORMHOLE wrap" signal -
    // captured here, at the smallest correct point, only once every lethal
    // destination check above has already passed and the wrapped head has
    // actually been committed to the body. `wrapped` is non-null only when
    // WORMHOLE was active for this move AND the raw head move crossed a
    // boundary (see `wrapIfOutOfBounds`) - a wrap that ends in death at the
    // destination returns from one of the three checks above and never
    // reaches this line, so it can never open a scoring window. PR13-C:
    // also mirrored onto `lastWormholeWrapSuccess` (see that field's own
    // doc) so `GameScene`'s wrap-flash rendering reads this exact value
    // instead of inferring a wrap from position deltas after the fact.
    const wormholeWrapSuccess = wrapped !== null;
    this.lastWormholeWrapSuccess = wormholeWrapSuccess;
    // Same "only a committed, non-lethal move counts" rule as
    // `lastWormholeWrapSuccess` above - a teleport that ends in death at
    // the destination never reaches this line.
    if (portalResolution) {
      this.lastPortalTraversal = { pairId: portalResolution.pairId, entry: preportalHead, exit: newHead };
    }

    const eatenBite = this.isGateOpenNow() ? undefined : this.bites.find((bite) => pointsEqual(bite.point, newHead));
    if (!eatenBite) {
      this.dropTail();
      this.recordManeuvers({ prevDirection, direction: requestedDirection, headBefore: head, headAfter: newHead, wormholeActiveForThisMove, wormholeWrapSuccess, eatenBiteKind: null, continuingStraightWouldBeLethal });
      // PR26 CHANGE 8: this real, executed, board-occupancy-changing move
      // just completed - retry a pending zero-fruit respawn now, using
      // the now-current snake position.
      this.retryPendingRespawnIfNeeded();
      return { type: "moved" };
    }

    const { growthDelta, event, speedTransitionMs } = this.applyBiteEffects(eatenBite);
    this.bites = [];

    if (growthDelta >= 1) {
      // The unshift above already grew the body by 1; only clone extra
      // tail segments for the remainder.
      this.growBy(growthDelta - 1);
    } else {
      this.dropTail(); // the normal move
      this.shrinkBy(-growthDelta);
    }

    // PR26 CHANGE 4: advance the Campaign-only post-95 RUSH continuation
    // for exactly this RUSH pick - after growth is resolved (immediately
    // above), before retargeting (immediately below), and ONLY for RUSH
    // (see `advancePostCapRushSpeed()`'s own doc for why SAFE/CHAOS/
    // STARFRUIT must never reach it).
    if (eatenBite.kind === "rush") {
      this.advancePostCapRushSpeed();
    }
    // Retarget the Speed Engine only now that the body is fully resolved
    // (growBy/dropTail/shrinkBy above already applied) - growth boost must
    // never count a segment before it actually exists in the snake. A
    // freshly-started SPEED TRIAL uses its own faster entry transition;
    // every other bite outcome uses the ordinary one (a no-op transition
    // restart if the target didn't actually change, e.g. WORMHOLE/SCORE
    // HIT, which don't touch length or debt at all).
    this.retargetSpeed(speedTransitionMs);
    // The RUSH toast's own speed readout can only be resolved now that
    // growth has actually applied - `applyBiteEffects()` ran before the
    // body was mutated, so it could not have known this RUSH's own +2
    // growth boost yet.
    const finalEvent: BiteFeedbackEvent = event.kind === "rush" ? { ...event, speedBoostPercent: this.currentEffectiveBoostPercent() } : event;

    // TRICK BITE/CLUTCH BITE (and the maneuver itself) are resolved here,
    // deliberately BEFORE gate opening below - "award the bite bonus
    // before gate opening or level completion is finalized" - even though
    // nothing gate-related reads Skill Score, so ordering here can never
    // matter for that reason either way.
    this.recordManeuvers({ prevDirection, direction: requestedDirection, headBefore: head, headAfter: newHead, wormholeActiveForThisMove, wormholeWrapSuccess, eatenBiteKind: eatenBite.kind, continuingStraightWouldBeLethal });

    // STARFRUIT always "just opens" the temporary gate; every other kind
    // may open the real, permanent gate via growth. Both are resolved
    // here, only now that the body is fully settled (growBy/dropTail
    // above already applied) - `currentLength()` would be transiently one
    // too high if read any earlier in this tick for a 0-growth bite.
    let justOpened: boolean;
    if (eatenBite.kind === "starfruit") {
      this.openTemporaryStarfruitGate();
      justOpened = true;
    } else {
      justOpened = this.checkGateOpen();
    }
    if (!justOpened && !this.speedTrial) {
      this.respawnTimerMs = GameConfig.bites.spawn.respawnDelayMs;
    }

    const length = this.snakeBody.length;
    return justOpened
      ? { type: "gate-opened", biteKind: eatenBite.kind, length, event: finalEvent }
      : { type: "ate", biteKind: eatenBite.kind, length, event: finalEvent };
  }

  /**
   * Scoring V2: reports one safe executed movement step to the
   * `ManeuverTracker` (using the fully-resolved post-move body - correct
   * tail-vacating semantics for every maneuver that cares about body
   * hazards) and, using that same step's result, to the
   * `TrickBiteTracker` - `eatenBiteKind` is the fruit (if any) eaten on
   * this exact step, `null` for a plain move or entering the gate. Never
   * called for a lethal move - "only safe executed movement can award a
   * skill maneuver or a bite bonus" holds by construction, since every
   * `game-over` branch above returns before this is ever reached.
   */
  private recordManeuvers(input: {
    prevDirection: Direction;
    direction: Direction;
    headBefore: Point;
    headAfter: Point;
    wormholeActiveForThisMove: boolean;
    /** PR13-B: true only on the exact step a WORMHOLE wrap was authoritatively committed this tick - see `tick()`'s own doc on where this is captured. Never fed into `ManeuverTracker` (WORMHOLE is an opener for TRICK/CLUTCH BITE, not one of the four geometric maneuvers) - only into `TrickBiteTracker`. */
    wormholeWrapSuccess: boolean;
    eatenBiteKind: BiteKind | null;
    /** PR26 CHANGE 1 (LAST-SECOND TURN V2): whether continuing straight in `prevDirection` from `headBefore`, evaluated against the PRE-this-tick state via production's own authoritative collision rules, would have been lethal - see `wouldContinuingStraightBeLethal()`. Captured in `tick()` before any of this tick's own mutations, since by the time this method runs the body/portal/WORMHOLE state may already reflect the turn actually taken. */
    continuingStraightWouldBeLethal: boolean;
  }): void {
    // Deliberately the Speed Engine's raw CURRENT interpolated interval,
    // never the separate gate-exit animation's `exitStepMs` (this is only
    // ever called for a genuine executed movement step, including the
    // gate-entry move itself, which happens the instant before `phase`
    // becomes "exiting" - see the call site above).
    const maneuverSpeedWeight = computeManeuverSpeedWeight(this.baseStepMs, this.speedEngine.currentStepMs);
    // PR26 LAST-SECOND TURN V3: deliberately NOT `this.speedEngine.currentStepMs`
    // (which interpolates toward SPEED TRIAL's fixed override while it's
    // active, and would let SPEED TRIAL "fake" a x4 reward) - this must be
    // the run's real persistent/underlying target, exactly what the engine
    // returns to once SPEED TRIAL ends. See `currentUnderlyingTargetStepMs()`'s
    // own doc and `computeLastSecondTurnSpeedMultiplier()`'s own doc for why.
    const lastSecondTurnSpeedMultiplier = computeLastSecondTurnSpeedMultiplier(this.baseStepMs, this.currentUnderlyingTargetStepMs());
    this.lastSkillMoment = this.maneuverTracker.recordMove({
      moveIndex: this.moveCount,
      prevDirection: input.prevDirection,
      direction: input.direction,
      headBefore: input.headBefore,
      headAfter: input.headAfter,
      bodyAfterMove: this.snakeBody,
      cols: this.cols,
      rows: this.rows,
      obstacleKeys: this.obstacleKeys,
      wormholeActive: input.wormholeActiveForThisMove,
      maneuverSpeedWeight,
      lastSecondTurnSpeedMultiplier,
      continuingStraightWouldBeLethal: input.continuingStraightWouldBeLethal,
    });
    this.lastBiteBonus = this.trickBiteTracker.recordStep({
      skillMoment: this.lastSkillMoment,
      fruitKind: input.eatenBiteKind,
      wormholeWrapSuccess: input.wormholeWrapSuccess,
    });
  }

  /** The skill moment (if any) resolved by the most recent `tick()` call - `null` if nothing was credited. Read this immediately after `tick()` returns, before calling `tick()` again. */
  getLastSkillMoment(): SkillMoment | null {
    return this.lastSkillMoment;
  }

  /** The TRICK/CLUTCH BITE (if any) awarded by the most recent `tick()` call - `null` if nothing was awarded. Read this immediately after `tick()` returns, same pattern as `getLastSkillMoment()`. */
  getLastBiteBonus(): BiteBonusResult | null {
    return this.lastBiteBonus;
  }

  /**
   * PR13-C: the single authoritative "did this `tick()` call just commit a
   * successful WORMHOLE boundary wrap" signal - see `lastWormholeWrapSuccess`'s
   * own doc. Read this immediately after `tick()` returns, same
   * read-once pattern as `getLastSkillMoment()`/`getLastBiteBonus()`.
   * Observational only - purely for rendering, never fed back into any
   * gameplay/scoring decision.
   */
  getLastWormholeWrapSuccess(): boolean {
    return this.lastWormholeWrapSuccess;
  }

  /**
   * Portals V1 (Saturn World): the portal traversal (if any) actually
   * committed by the most recent `tick()` call - `null` if none. Read
   * this immediately after `tick()` returns, same read-once pattern as
   * `getLastWormholeWrapSuccess()`. Observational only - purely for
   * rendering/analytics, never fed back into any gameplay/scoring decision.
   */
  getLastPortalTraversal(): { pairId: string; entry: Point; exit: Point } | null {
    return this.lastPortalTraversal;
  }

  /**
   * Advances real-time-only state: an active WORMHOLE/STARFRUIT-gate
   * countdown, the SPEED TRIAL countdown, and the ~250ms delay before a
   * new bite group appears. Independent of grid ticks so it stays correct
   * regardless of the level's step speed, and must simply not be called
   * while paused or mid-exit for all of it to freeze.
   */
  advanceRealTime(deltaMs: number): LevelRealTimeEvent | null {
    if (this.phase !== "playing") return null;
    this.activeElapsedMs += deltaMs;

    if (this.wormhole) {
      this.wormhole.remainingMs -= deltaMs;
      if (this.wormhole.remainingMs <= 0) this.wormhole = null;
    }

    if (this.starfruitGate) {
      this.starfruitGate.remainingMs -= deltaMs;
      if (this.starfruitGate.remainingMs <= 0) {
        this.starfruitGate = null;
        if (this.currentLength() >= this.targetLength) {
          // Defensive: the real target was reached while the temporary
          // gate was open (shouldn't normally happen - no bites spawn
          // during this window - but the engine must not depend on that).
          // Promote to permanent rather than closing.
          this.gatePermanentlyOpen = true;
          this.completionReason = "length_target";
          this.finalLength = this.currentLength();
        } else {
          // Not complete yet - close the temporary gate and resume normal play.
          this.completionReason = null;
          this.finalLength = null;
          this.respawnTimerMs = GameConfig.bites.spawn.respawnDelayMs;
          this.retargetSpeed(SPEED_TRANSITION_MS.ordinary);
          this.speedEngine.advance(deltaMs);
          return { kind: "starfruit-gate-expired" };
        }
      }
    }

    if (this.speedTrial) {
      this.speedTrial.remainingMs -= deltaMs;
      if (this.speedTrial.remainingMs > 0) {
        // Still counting down - growth/pressure can still have changed the
        // TEMPORARY target this frame (e.g. a pressure stage crossed while
        // the trial runs), so still retarget/advance before returning.
        this.retargetSpeed(SPEED_TRANSITION_MS.ordinary);
        this.speedEngine.advance(deltaMs);
        return null;
      }

      this.speedTrial = null;
      const { rewardGrowth, rewardScore } = GameConfig.bites.chaos.speedTrial;
      this.growBy(rewardGrowth);
      this.score += rewardScore;
      const justOpened = this.checkGateOpen();
      if (!justOpened) {
        this.respawnTimerMs = GameConfig.bites.spawn.respawnDelayMs;
      }
      // Ending SPEED TRIAL: return to the current accumulated
      // non-temporary target (growth/RUSH debt/HIGH SPEED/pressure - the
      // reward growth above is already reflected, since retargetSpeed()
      // reads the now-fully-resolved body) over the dedicated exit
      // transition - never a jump, and never erases RUSH debt.
      this.retargetSpeed(SPEED_TRANSITION_MS.speedTrialExit);
      this.speedEngine.advance(deltaMs);
      return { kind: "speed-trial-success", rewardGrowth, rewardScore };
    }

    if (this.respawnTimerMs > 0 && !this.isGateOpenNow()) {
      this.respawnTimerMs -= deltaMs;
      if (this.respawnTimerMs <= 0) {
        this.respawnTimerMs = 0;
        const spawned = this.spawnBitesNow(false);
        // Endless Growth V1 only: a normal post-consumption/post-respawn
        // spawn attempt (this is the only such attempt outside `reset()`'s
        // own initial spawn, and SPEED TRIAL's branch above already
        // returns before ever reaching here) found zero valid cells on
        // the fixed arena - end the run instead of silently leaving the
        // board empty. Every other survival level (`completeWhenNoBitesCanSpawn`
        // defaults `false`) keeps today's behavior exactly.
        if (spawned.length === 0 && this.completeWhenNoBitesCanSpawn) {
          this.completeArenaCleared();
          return { kind: "arena-cleared" };
        }
        // PR26 CHANGE 8: every other level (Campaign, Daily survival)
        // that finds zero legal cells right now does NOT abandon
        // spawning and does NOT auto-complete - it marks the respawn
        // pending and retries after the snake's next legal move actually
        // changes board occupancy (`retryPendingRespawnIfNeeded()`,
        // called from `tick()`). `respawnTimerMs` stays at 0 either way -
        // no fresh 250ms wait starts on a failed attempt or its retries.
        this.awaitingSpawnRetry = spawned.length === 0;
        this.bites = spawned;
      }
    }
    // Daily's pressure ramp can shift the target purely from elapsed
    // time, with no bite involved at all - re-check every call (this is a
    // no-op transition restart whenever the computed target hasn't
    // actually changed, e.g. every Campaign call, or a Daily call inside
    // the same pressure stage).
    this.retargetSpeed(SPEED_TRANSITION_MS.ordinary);
    this.speedEngine.advance(deltaMs);
    return null;
  }

  // ---------------------------------------------------------------------
  // Bite resolution
  // ---------------------------------------------------------------------

  private applyBiteEffects(bite: Bite): { growthDelta: number; event: BiteFeedbackEvent; speedTransitionMs: number } {
    // PR13-A (WORMHOLE lifecycle rules): eating ANY fruit - SAFE, RUSH,
    // CHAOS (including a fresh JACKPOT/SHRINK/SPEED TRIAL/WORMHOLE-again
    // roll), or STARFRUIT - no longer ends an active WORMHOLE. Only its
    // own natural 10s expiry (`advanceRealTime()`), a fresh WORMHOLE roll
    // (refreshes to exactly 10000ms - see the `"wormhole"` case below,
    // non-additive), or gate ENTRY/EXITING (never gate OPENING - see
    // `checkGateOpen()`) end it now. See `wormhole.test.ts` for the full
    // lifecycle matrix this enables (WORMHOLE surviving every other bite
    // kind, coexisting with SPEED TRIAL, etc).
    switch (bite.kind) {
      case "safe": {
        // Ends the RUSH SCORING COMBO only - accumulated RUSH speed debt
        // (cumulativeYellowCount) is untouched. SAFE has no speed effect
        // of its own; it accelerates only through its own +1 real length,
        // via the ordinary growth-boost path every bite goes through.
        this.resetCombo();
        const { growth, score } = GameConfig.bites.safe;
        this.score += score;
        return { growthDelta: growth, event: { kind: "safe", growth, score }, speedTransitionMs: SPEED_TRANSITION_MS.ordinary };
      }
      case "rush": {
        this.cumulativeYellowCount += 1;
        this.rushComboUncapped += 1;
        this.rushComboStack = Math.min(this.rushComboUncapped, GameConfig.bites.rush.maxStack);
        const { growth, scoreByStack } = GameConfig.bites.rush;
        const score = scoreByStack[this.rushComboStack - 1];
        this.score += score;
        return {
          growthDelta: growth,
          // `speedBoostPercent` is patched by `tick()` once growth has
          // actually applied to the body - this method runs before that
          // mutation, so it cannot compute the true post-growth value yet.
          event: { kind: "rush", stack: this.rushComboStack, growth, score, speedBoostPercent: 0 },
          speedTransitionMs: SPEED_TRANSITION_MS.ordinary,
        };
      }
      case "chaos": {
        // Ends the RUSH SCORING COMBO only, for every CHAOS outcome
        // (including JACKPOT) - accumulated RUSH speed debt is never
        // erased here, whatever the outcome turns out to be.
        this.resetCombo();
        const outcome = this.resolveChaosOutcome();
        return this.applyChaosOutcome(outcome);
      }
      case "starfruit": {
        // Ends the RUSH SCORING COMBO only - same as SAFE/CHAOS. Never
        // touches RUSH speed debt.
        // Deliberately does NOT call openTemporaryStarfruitGate() here: at
        // this point in tick() the new head has been unshifted but the
        // tail hasn't been dropped yet (0 growth), so currentLength() is
        // transiently one too high. tick() calls it itself, after the
        // body is fully resolved - see the `eatenBite.kind === "starfruit"` branch there.
        this.resetCombo();
        const gateDurationSeconds = Math.round(GameConfig.starfruit.gateDurationMs / 1000);
        return { growthDelta: 0, event: { kind: "starfruit", gateDurationSeconds }, speedTransitionMs: SPEED_TRANSITION_MS.ordinary };
      }
    }
  }

  /**
   * Boundaries are computed from rounded whole-percent thresholds, not by
   * accumulating the raw fractional weights - plain repeated float
   * addition (0.25 + 0.2 + 0.2 + 0.2) lands on 0.8500000000000001, not
   * 0.85, which would silently shift the SHRINK/SPEED TRIAL boundary by a
   * floating-point rounding artifact instead of the documented value.
   */
  private resolveChaosOutcome(): ChaosOutcomeKind {
    if (this.qaForcedChaosOutcome) {
      const forced = this.qaForcedChaosOutcome;
      this.qaForcedChaosOutcome = null;
      return forced;
    }
    return resolveChaosOutcome(this.chaosRandom, this.chaosRulesVersion);
  }

  private applyChaosOutcome(outcome: ChaosOutcomeKind): { growthDelta: number; event: BiteFeedbackEvent; speedTransitionMs: number } {
    const chaos = GameConfig.bites.chaos;
    switch (outcome) {
      case "jackpot": {
        // CHAOS itself adds no permanent speed - only its actual length
        // change does, via the ordinary growth-boost path (JACKPOT's own
        // +3 real length here).
        const { growth, score } = chaos.jackpot;
        this.score += score;
        return { growthDelta: growth, event: { kind: "chaos", outcome, growth, score }, speedTransitionMs: SPEED_TRANSITION_MS.ordinary };
      }
      case "wormhole": {
        // No length change - no speed effect, ordinary transition is a no-op.
        const { durationMs } = chaos.wormhole;
        // PR26 CHANGE 7: a genuinely NEW activation (WORMHOLE was not
        // already active) starts the WORMHOLE-sourced TRICK/CLUTCH
        // reward-scaling counter over at 0 - a REFRESH of an already-
        // active WORMHOLE (this.wormhole already non-null) is the same
        // activation continuing, so the counter is deliberately left
        // untouched. Checked BEFORE the assignment below overwrites it.
        if (this.wormhole === null) {
          this.trickBiteTracker.resetWormholeActivationCounter();
        }
        this.wormhole = { remainingMs: durationMs };
        return { growthDelta: 0, event: { kind: "chaos", outcome, durationSeconds: Math.round(durationMs / 1000) }, speedTransitionMs: SPEED_TRANSITION_MS.ordinary };
      }
      case "speed-trial": {
        const { durationMs, rewardGrowth, rewardScore } = chaos.speedTrial;
        this.speedTrial = { remainingMs: durationMs };
        return {
          growthDelta: 0,
          event: {
            kind: "chaos",
            outcome,
            durationSeconds: Math.round(durationMs / 1000),
            rewardGrowth,
            rewardScore,
          },
          // The one case that composes a genuinely new (temporary) boost
          // on top of whatever growth/RUSH debt/HIGH SPEED already exist -
          // uses the dedicated, snappier entry transition, never the
          // ordinary one.
          speedTransitionMs: SPEED_TRANSITION_MS.speedTrialEntry,
        };
      }
      case "score-hit": {
        const result = computeScoreHit(this.score);
        this.score = result.newScore;
        return { growthDelta: 0, event: { kind: "chaos", outcome, ...result }, speedTransitionMs: SPEED_TRANSITION_MS.ordinary };
      }
      case "shrink": {
        const { segments, scoreLoss } = chaos.shrink;
        // Same no-floor rule as SCORE HIT - see above.
        this.score -= scoreLoss;
        const originalLength = this.snakeBody.length - 1; // pre-move length: the head was already unshifted this tick
        const removable = Math.max(0, originalLength - this.level.initialLength);
        const segmentsRemoved = Math.min(segments, removable);
        // SHRINK reduces only the growth-derived boost (the body is
        // genuinely shorter now) - RUSH debt (cumulativeYellowCount) is
        // never touched here, so it survives intact. The ordinary
        // transition smoothly eases the resulting slowdown, same as any
        // other target change.
        return { growthDelta: -segments, event: { kind: "chaos", outcome, segmentsRemoved, scoreLoss }, speedTransitionMs: SPEED_TRANSITION_MS.ordinary };
      }
    }
  }

  /**
   * The single source of truth for how far the snake has actually grown -
   * every growth path (SAFE, RUSH, CHAOS JACKPOT, a completed SPEED
   * TRIAL) applies via `growBy`/`shrinkBy` synchronously, directly onto
   * `snakeBody`, before this is ever read - there is no separate
   * "pending" growth buffered anywhere. The HUD (`getSnapshot().length`)
   * and the gate check below both read this exact same value, so they
   * can never disagree about progress.
   */
  private currentLength(): number {
    return this.snakeBody.length;
  }

  /** True while either the permanent or the temporary (STARFRUIT) gate is open. */
  private isGateOpenNow(): boolean {
    return this.gatePermanentlyOpen || this.starfruitGate !== null;
  }

  /**
   * The permanent-gate check, called after every path that can grow the
   * snake (SAFE/RUSH/JACKPOT via `tick()`, a completed SPEED TRIAL via
   * `advanceRealTime()`). Opens exactly once, the instant `currentLength()`
   * reaches `targetLength` - no extra bite or step required - and clears
   * any in-flight bites/timers so nothing can spawn or reward after the
   * gate is open. Never re-closes once open (SHRINK, the only path that
   * can reduce length, can't run after the gate opens - no bites exist to
   * trigger it).
   */
  private checkGateOpen(): boolean {
    // Survival's one rule: the gate can never spawn, open, or become
    // reachable, at any length, ever - "there is no Daily target length"
    // - so this is checked structurally here, not simulated by inflating
    // `targetLength`. Every caller of this method already only cares
    // about its boolean result (whether to skip the respawn timer this
    // tick); returning false unconditionally means `gatePermanentlyOpen`
    // can never become true for a survival run, so `isGateOpenNow()`,
    // `renderGate()`-equivalent UI, and the gate-entry branch in `tick()`
    // are all unreachable for the whole run's lifetime.
    if (this.objective === "survival") return false;
    if (this.gatePermanentlyOpen) return false;
    if (this.currentLength() < this.targetLength) return false;
    this.gatePermanentlyOpen = true;
    // The place `finalLength`/`completionReason` are set for a real,
    // length-target completion - captures the actual length that earned
    // completion (>= targetLength; a single bite's growth can overshoot
    // it) before anything - the exit walk included - can change
    // `snakeBody.length` again.
    this.finalLength = this.currentLength();
    this.completionReason = "length_target";
    this.starfruitGate = null;
    this.bites = [];
    this.respawnTimerMs = 0;
    // PR11 (Speed Engine V2): opening the gate must never alter speed
    // state - growth boost, RUSH debt, the scoring combo, current speed,
    // and target speed all carry straight through unchanged. This used to
    // null out `speedTrial` here too (confusing, and unreachable in
    // practice: bites are empty while a trial is genuinely active, so a
    // real bite-eat can never reach this method mid-trial) - removed.
    // PR13-A (WORMHOLE lifecycle rules): opening the PERMANENT gate must
    // never end an active WORMHOLE either - gate OPENING and gate ENTRY
    // are deliberately different lifecycle events; only ENTRY (stepping
    // onto the exit cell, in `tick()`'s EXITING branch) clears it. Used to
    // null out `wormhole` here too - removed.
    return true;
  }

  /** STARFRUIT's temporary gate key: opens the gate for `GameConfig.starfruit.gateDurationMs`, freezing `finalLength` at the current (unchanged - STARFRUIT never grows the snake) length. No bites spawn while this is open, so the length can't drift before either EXITING or expiry. */
  private openTemporaryStarfruitGate(): void {
    this.starfruitGate = { remainingMs: GameConfig.starfruit.gateDurationMs };
    this.finalLength = this.currentLength();
    this.completionReason = "starfruit_gate";
    this.respawnTimerMs = 0;
  }

  /**
   * Endless Growth V1's opt-in "ARENA CLEARED" completion - a fixed arena
   * cannot support literal infinite growth, so a normal post-consumption/
   * post-respawn spawn attempt that finds zero valid cells ends the run
   * here (see the call site in `advanceRealTime()`). Deliberately a
   * DIFFERENT phase/result from both "game-over" and "level-complete" -
   * `completionReason` is deliberately left `null` (never `"length_target"`
   * or `"starfruit_gate"`) - so nothing keyed off any of those (Campaign
   * star/progress/leaderboard/exit-walk code, Daily's finish path) can
   * ever fire for it.
   */
  private completeArenaCleared(): void {
    this.phase = "arena-cleared";
    this.finalLength = this.currentLength();
    this.bites = [];
    this.respawnTimerMs = 0;
    this.trickBiteTracker.clearWindow();
  }

  /**
   * `isInitialSpawn` gates `initialFruitExclusionKeys` (Daily V6's
   * mandatory safe-opening cells, section 8) - `true` only from
   * `reset()`'s own initial spawn, `false` from every subsequent
   * respawn-timer call, so the exclusion applies exactly once per run and
   * never touches the global 250ms respawn rule.
   */
  private spawnBitesNow(isInitialSpawn: boolean): Bite[] {
    this.fruitGroupCount += 1;
    const blocked = new Set<string>(this.obstacleKeys);
    for (const key of this.snakeKeys) blocked.add(key);
    blocked.add(cellKey(this.exit));
    // Portals V1 (Saturn World): a fruit (SAFE/RUSH/CHAOS/STARFRUIT) must
    // never spawn on a cell that also teleports the player - `[]`/empty
    // for every level without `portals`, so this is a no-op everywhere
    // else. Only the exact portal cells are excluded; surrounding cells
    // remain ordinary spawn territory, and the reachability BFS inside
    // `spawnBites()`/`findStarfruitCell()` is deliberately left
    // portal-unaware (every Saturn V1 region stays reachable without a
    // portal - see `LevelDefinition.portals`'s doc).
    for (const key of this.portalEndpointKeys) blocked.add(key);
    // BLACK HOLE V1 (Saturn World): same rule as portal cells - a fruit
    // must never spawn on a cell that's also instantly lethal. `[]`/empty
    // for every level without `blackHoles`.
    for (const key of this.blackHoleKeys) blocked.add(key);
    // Daily V6 only (PR25, section 8): the mandatory 4-cell safe opening
    // must stay fruit-free for the INITIAL spawn only - `[]`/empty for
    // every other level, so this is a no-op everywhere else.
    if (isInitialSpawn) {
      for (const key of this.initialFruitExclusionKeys) blocked.add(key);
    }

    const regular = spawnBites(this.snakeBody[0], this.cols, this.rows, blocked, this.random, this.allowedBiteKinds);
    if (regular.length === 0) {
      // PR26 CHANGE 8: a totally failed spawn attempt (zero legal cells)
      // never actually placed a group - undo the increment above so
      // repeated pending-respawn retries (`retryPendingRespawnIfNeeded()`)
      // can never inflate the STARFRUIT reveal-group counter past what a
      // real spawned group would have reached.
      this.fruitGroupCount -= 1;
      return regular;
    }
    if (!this.shouldRevealStarfruitThisGroup()) return regular;

    const starPoint = findStarfruitCell(
      this.snakeBody[0],
      this.cols,
      this.rows,
      blocked,
      regular.map((b) => b.point),
      this.random,
    );
    // Bounded search failed (no legal 4th cell) - preserve the regular
    // trio exactly as already placed, and skip STARFRUIT for this run:
    // `fruitGroupCount` never equals `revealGroupNumber` again, so this
    // is never retried.
    if (!starPoint) return regular;

    this.starfruitSpawnedThisRun = true;
    this.qaForceStarfruitNextSpawn = false;
    return [...regular, { kind: "starfruit", point: starPoint }];
  }

  /**
   * PR26 CHANGE 8 (Campaign zero-fruit soft-lock fix): retries a pending
   * respawn - a no-op unless `awaitingSpawnRetry` is set (see that
   * field's own doc). Called from `tick()`'s plain-move branch, i.e.
   * after the snake's board occupancy has just genuinely changed via a
   * real executed move - never from `advanceRealTime()`, since the mere
   * passage of real time can never free up a cell on its own. Uses the
   * exact same `spawnBitesNow()` and its unmodified safety invariants
   * (never spawns on the snake/obstacles/BLACK HOLE/portal cells/any
   * currently-forbidden cell) - still finding nothing simply leaves
   * `awaitingSpawnRetry` set for the next move, with no fresh 250ms wait.
   * The gate-open check is a defensive safeguard (structurally this state
   * can't currently coexist with an open gate, since nothing can be eaten
   * to grow toward the target while `bites` is empty) - if the gate is
   * somehow already open, fruit should no longer exist at all, so this
   * simply clears the pending flag without spawning.
   */
  private retryPendingRespawnIfNeeded(): void {
    if (!this.awaitingSpawnRetry) return;
    if (this.isGateOpenNow()) {
      this.awaitingSpawnRetry = false;
      return;
    }
    const spawned = this.spawnBitesNow(false);
    if (spawned.length === 0) return;
    this.bites = spawned;
    this.awaitingSpawnRetry = false;
  }

  private shouldRevealStarfruitThisGroup(): boolean {
    if (this.qaForceStarfruitNextSpawn && !this.starfruitSpawnedThisRun) return true;
    return (
      this.starfruitEligible &&
      !this.starfruitSpawnedThisRun &&
      this.fruitGroupCount === GameConfig.starfruit.revealGroupNumber
    );
  }

  // ---------------------------------------------------------------------
  // Gate exit walk
  // ---------------------------------------------------------------------

  private exitStep(): LevelTickResult {
    const removed = this.snakeBody.shift();
    if (removed) this.snakeKeys.delete(cellKey(removed));
    if (this.snakeBody.length === 0) {
      this.phase = "level-complete";
      return { type: "level-complete" };
    }
    return { type: "exiting", length: this.snakeBody.length };
  }

  // ---------------------------------------------------------------------
  // Body helpers
  // ---------------------------------------------------------------------

  private dropTail(): void {
    const tail = this.snakeBody.pop();
    if (tail) this.snakeKeys.delete(cellKey(tail));
  }

  private growBy(count: number): void {
    for (let i = 0; i < count; i++) {
      const tail = this.snakeBody[this.snakeBody.length - 1];
      this.snakeBody.push({ ...tail });
      this.snakeKeys.add(cellKey(tail));
    }
  }

  /** Never shrinks below the level's starting length. */
  private shrinkBy(count: number): void {
    for (let i = 0; i < count; i++) {
      if (this.snakeBody.length <= this.level.initialLength) break;
      this.dropTail();
    }
  }

  /** Ends the run lethally. PR13-B: also clears the Trick/Clutch window (whichever source) - a window opened by an earlier tick must never be readable as "still active" once the run is over; already-awarded bonuses/counters are untouched, same as `TrickBiteTracker.clearWindow()`'s own guarantee. */
  private die(reason: "wall" | "obstacle" | "self" | "black_hole"): LevelTickResult {
    this.phase = "game-over";
    this.gameOverReason = reason;
    this.trickBiteTracker.clearWindow();
    return { type: "game-over", reason };
  }

  private hitsWall(point: Point): boolean {
    return point.x < 0 || point.x >= this.cols || point.y < 0 || point.y >= this.rows;
  }

  private isObstacle(point: Point): boolean {
    return this.obstacleKeys.has(cellKey(point));
  }

  /** BLACK HOLE V1 (Saturn World) - see `LevelDefinition.blackHoles`'s doc. Always `false` for every level without `blackHoles`. */
  private isBlackHole(point: Point): boolean {
    return this.blackHoleKeys.has(cellKey(point));
  }

  private hitsBody(point: Point): boolean {
    return this.snakeKeys.has(cellKey(point));
  }

  /**
   * WORMHOLE edge-wrapping: `null` when `point` is already in bounds
   * (nothing to wrap), otherwise the corresponding cell on the opposite
   * edge (left<->right, top<->bottom). Only ever called while WORMHOLE is
   * active - internal obstacles/self-collision are checked separately,
   * against the returned (already in-bounds) point, by the normal rules
   * in `tick()`.
   */
  private wrapIfOutOfBounds(point: Point): Point | null {
    const outOfBounds = point.x < 0 || point.x >= this.cols || point.y < 0 || point.y >= this.rows;
    if (!outOfBounds) return null;
    return {
      x: ((point.x % this.cols) + this.cols) % this.cols,
      y: ((point.y % this.rows) + this.rows) % this.rows,
    };
  }

  /**
   * JUNCTION gameplay-QA pass (`PortalNetwork`): picks one of `candidates`
   * uniformly at random via `portalRandom`. `candidates` already excludes
   * the endpoint the head just entered (built once in `buildPortalLookup()`),
   * so this can never resolve back to it. `Math.min(candidates.length - 1, ...)`
   * guards the (astronomically unlikely but not impossible for a raw
   * `Math.random()`-like source) case of `portalRandom()` returning exactly
   * `1` rounding up to an out-of-range index.
   */
  private pickRandomPortalDestination(candidates: readonly Point[]): Point {
    const index = Math.min(candidates.length - 1, Math.floor(this.portalRandom() * candidates.length));
    return candidates[index];
  }

  /**
   * PR26 CHANGE 1 (LAST-SECOND TURN V2): would continuing straight in
   * `prevDirection` from `headBefore`, under production's own
   * authoritative collision rules, have killed the snake? Read-only -
   * never mutates state, never consumes RNG - and deliberately reuses the
   * EXACT SAME resolution order and private helpers `tick()` itself uses
   * (gate priority -> WORMHOLE wrap -> portal resolution -> wall/obstacle/
   * BLACK HOLE/self), never a simplified "is the next cell currently
   * occupied" check. In particular `hitsBody()` is called completely
   * unmodified - this game's own tested self-collision rule already
   * treats entering the CURRENT tail cell as lethal regardless of whether
   * it would vacate on a non-growing move (see e.g. `wormhole.test.ts`'s
   * "wraps directly onto the tail - lethal" case) - so reusing `hitsBody`
   * as-is, against the untouched pre-tick `snakeKeys`, is exactly the
   * correct tail-vacating semantics, not an approximation of them.
   *
   * A random-network portal's destination cannot be determined here
   * without consuming `portalRandom` (which would desync the real run's
   * RNG stream from what actually happens this tick, since this straight
   * continuation never actually occurs). Since entering a portal is never
   * itself lethal, this one case is conservatively treated as safe,
   * exactly like a fixed portal's own entry - "valid fixed-portal
   * traversal" is a documented non-qualifying case, and a random-network
   * traversal is never assumed to be *more* dangerous than that without
   * evidence.
   */
  private wouldContinuingStraightBeLethal(headBefore: Point, prevDirection: Direction, wormholeActiveForThisMove: boolean): boolean {
    const rawNewHead = stepInDirection(headBefore, prevDirection);
    if (this.isGateOpenNow() && pointsEqual(rawNewHead, this.exit)) return false;
    const wrapped = wormholeActiveForThisMove ? this.wrapIfOutOfBounds(rawNewHead) : null;
    const preportalHead = wrapped ?? rawNewHead;
    const portalResolution = resolvePortalTraversal(preportalHead, this.portalLookup);
    if (portalResolution && portalResolution.kind === "random") return false;
    const resolvedHead = portalResolution ? portalResolution.to : preportalHead;
    return this.hitsWall(resolvedHead) || this.isObstacle(resolvedHead) || this.isBlackHole(resolvedHead) || this.hitsBody(resolvedHead);
  }

  // ---------------------------------------------------------------------
  // Speed (PR11 - Speed Engine V2). See `src/core/SpeedEngine.ts` for the
  // pure composition math this section is a thin, stateful wrapper
  // around. `resetCombo()` is the ONLY reset operation exposed here - it
  // is structurally impossible for it to also touch `cumulativeYellowCount`
  // (RUSH speed debt), since that field is private to this class and
  // `resetCombo()`'s body simply never mentions it.
  // ---------------------------------------------------------------------

  /** `baseStepMs` after Daily's pressure ramp - a no-op (returns `baseStepMs` unchanged) whenever `pressure` is absent (every Campaign level). Exactly as pause-aware as `activeElapsedMs` itself. */
  private currentPressureAdjustedBaseMs(): number {
    return computePressureAdjustedBaseMs(this.baseStepMs, this.pressure, this.activeElapsedMs);
  }

  /** Growth-derived boost from the snake's ACTUAL current length relative to this run's starting length - never a pending/not-yet-applied amount. */
  private currentGrowthBoost(): number {
    return computeGrowthBoost(Math.max(0, this.currentLength() - this.level.initialLength));
  }

  /** Permanent RUSH debt from this run's cumulative yellow-fruit count - never reset by SAFE/CHAOS/STARFRUIT/gate-opening. */
  private currentRushDebtBoost(): number {
    return computeRushDebtBoost(this.cumulativeYellowCount, this.rushDebtTiers);
  }

  /**
   * The UNDERLYING composed target: base (pressure-adjusted for Daily) +
   * Growth boost + RUSH debt + persistent mode boost, capped by the
   * NORMAL cap - deliberately never the temporary cap, and deliberately
   * never fed a SPEED TRIAL contribution (PR11.1: SPEED TRIAL no longer
   * composes a percentage boost at all - see `currentFixedOverrideStepMs()`).
   * This is what the real, permanent, accumulated run state adds up to
   * RIGHT NOW, independent of whether a temporary fixed override happens
   * to be active on top of it - i.e. exactly what SPEED TRIAL returns to
   * when it ends.
   */
  private currentUnderlyingTargetStepMs(): number {
    const uncapped = computeUncappedTargetStepMs({
      pressureAdjustedBaseMs: this.currentPressureAdjustedBaseMs(),
      growthBoost: this.currentGrowthBoost(),
      rushDebtBoost: this.currentRushDebtBoost(),
      persistentModeBoost: this.persistentModeBoost,
      temporarySpeedBoost: 0,
    });
    const engineDerived = this.applyCampaignAwareSpeedCap(uncapped);
    // Daily V6 only (PR25): `this.pressureCurve` is `undefined` for every
    // V1-V5 Daily spec and every Campaign/Endless level, so this branch
    // never fires for them - `engineDerived` above (composed exactly the
    // same way it always was, since `this.pressure` is also `undefined`
    // for a V6 level) is returned unchanged. For V6, pressure instead
    // combines with the engine-derived speed via `computeV6EffectiveStepMs()` -
    // "preserve the faster of player-derived speed and pressure", never
    // an additive stack on top of growth/RUSH (see `SpeedEngine.ts`'s doc).
    if (this.pressureCurve) {
      return computeV6EffectiveStepMs(engineDerived, this.pressureCurve, this.baseStepMs, this.activeElapsedMs, this.pressureCurve.ceilingMs);
    }
    return engineDerived;
  }

  /**
   * PR26 CHANGE 4: applies the normal speed cap, except for Campaign once
   * the post-95 RUSH continuation has engaged (`postCapRushStepMs !== null`),
   * where that value is authoritative instead of a flat 95ms plateau -
   * see `advancePostCapRushSpeed()`. Daily/Endless are completely
   * unaffected: `postCapRushStepMs` stays `null` forever for them (that
   * method no-ops for any non-Campaign `speedMode`), so this always falls
   * through to the exact same `applySpeedCap()` call every mode has
   * always used.
   */
  private applyCampaignAwareSpeedCap(uncapped: number): number {
    if (this.speedMode === "campaign" && this.postCapRushStepMs !== null) {
      return this.postCapRushStepMs;
    }
    return applySpeedCap(uncapped, this.speedCaps.normalMs);
  }

  /**
   * PR26 CHANGE 4: advances the Campaign-only post-95ms RUSH speed
   * continuation. Called exactly once per RUSH pick, in `tick()`, only
   * after this pick's growth has been fully applied to the body (so
   * `currentGrowthBoost()` reflects it) and only for `speedMode ===
   * "campaign"` - a deliberate no-op for every other mode, so Daily/
   * Endless's own existing cap logic is untouched byte-for-byte.
   *
   * Two phases, matching the design spec exactly:
   *  - Not yet engaged (`postCapRushStepMs === null`): stays `null` until
   *    the ordinary composed (uncapped) target first drops to <=95ms on a
   *    RUSH pick - "the RUSH that first reaches the existing 95ms floor
   *    may land at 95ms as today." Engaging sets it to exactly the normal
   *    cap (95), never to `uncapped` itself.
   *  - Already engaged: every ADDITIONAL RUSH pick closes 15% of the
   *    remaining distance from the current post-cap value to the
   *    absolute 75ms floor (`next = 75 + (current - 75) * 0.85`) -
   *    asymptotic, so it can never reach or cross 75ms in finitely many
   *    picks; `Math.max` below is a defensive, not load-bearing, floor.
   *
   * Deliberately the ONLY place that ever writes `postCapRushStepMs` -
   * SAFE/CHAOS/STARFRUIT/SPEED-TRIAL-completion never call this, so none
   * of them can ever erase or advance it (SPEED TRIAL's own +3 growth
   * reward reaches `currentGrowthBoost()` like any other growth, but
   * never re-engages or advances this mechanism, and never adds RUSH
   * debt - `cumulativeYellowCount` is untouched by anything but an actual
   * RUSH pick).
   */
  private advancePostCapRushSpeed(): void {
    if (this.speedMode !== "campaign") return;
    const uncapped = computeUncappedTargetStepMs({
      pressureAdjustedBaseMs: this.currentPressureAdjustedBaseMs(),
      growthBoost: this.currentGrowthBoost(),
      rushDebtBoost: this.currentRushDebtBoost(),
      persistentModeBoost: this.persistentModeBoost,
      temporarySpeedBoost: 0,
    });
    if (this.postCapRushStepMs === null) {
      if (uncapped <= this.speedCaps.normalMs) {
        this.postCapRushStepMs = this.speedCaps.normalMs;
      }
      return;
    }
    const floor = SPEED_TRIAL_FIXED_STEP_MS; // 75ms - the same absolute minimum SPEED TRIAL uses.
    this.postCapRushStepMs = Math.max(floor, floor + (this.postCapRushStepMs - floor) * 0.85);
  }

  /**
   * The authorized FIXED movement override: exactly `SPEED_TRIAL_FIXED_STEP_MS`
   * (75ms) while SPEED TRIAL is active, clamped by the temporary cap as a
   * defensive floor (the two are equal by design - see
   * `GameConfig.speedEngine`'s doc); `null` otherwise, meaning "no
   * override, use the underlying target". Never derived from
   * Growth/RUSH debt/Daily pressure/HIGH SPEED, and never derived from
   * whatever the underlying target happened to be at trial entry.
   */
  private currentFixedOverrideStepMs(): number | null {
    if (!this.speedTrial) return null;
    return applySpeedCap(SPEED_TRIAL_FIXED_STEP_MS, this.speedCaps.temporaryMs);
  }

  /** The cap CURRENTLY in force - the temporary cap while SPEED TRIAL is active, the normal cap otherwise. QA/display only; the underlying target always uses the normal cap regardless (see `currentUnderlyingTargetStepMs()`). */
  private activeSpeedCapMs(): number {
    return this.speedTrial ? this.speedCaps.temporaryMs : this.speedCaps.normalMs;
  }

  /** The FINAL effective target - what the Speed Engine should actually transition toward RIGHT NOW: the fixed override when SPEED TRIAL is active, otherwise the underlying composed-and-capped target. */
  private computeCappedTargetStepMs(): number {
    return resolveEffectiveTargetStepMs(this.currentUnderlyingTargetStepMs(), this.currentFixedOverrideStepMs());
  }

  /** The total composed boost currently in effect, as a cap-aware whole-number percent - what the HUD/toasts display. Naturally reflects SPEED TRIAL's fixed 75ms target too (as whatever real ratio that happens to be against the reference base), never a fabricated "+35%". */
  private currentEffectiveBoostPercent(): number {
    return computeEffectiveBoostPercent(this.currentPressureAdjustedBaseMs(), this.computeCappedTargetStepMs());
  }

  /**
   * Recomputes the target step interval from every current input and
   * pushes it into the Speed Engine with the given transition duration -
   * call this after ANY state change that can affect speed (growth,
   * RUSH debt, SPEED TRIAL start/end, a Daily pressure stage crossing).
   * A no-op transition restart whenever the computed target hasn't
   * actually changed (`SpeedEngine.setTarget()`'s own short-circuit), so
   * calling this defensively/unconditionally is always safe. Never
   * called while `phase === "exiting"` - the gate-exit walk is a wholly
   * separate, non-gameplay animation-speed system (`computeExitStepMs()`
   * below), which never reads or writes the Speed Engine at all.
   */
  private retargetSpeed(transitionMs: number): void {
    if (this.phase === "exiting") return;
    this.speedEngine.setTarget(this.computeCappedTargetStepMs(), transitionMs);
  }

  /** Ends the RUSH SCORING COMBO only (`rushComboStack`/`rushComboUncapped`) - never `cumulativeYellowCount` (RUSH speed debt), which this method's body simply cannot reach. Called by SAFE/CHAOS/STARFRUIT. */
  private resetCombo(): void {
    this.rushComboStack = 0;
    this.rushComboUncapped = 0;
  }

  /** The interval movement ticks must actually use right now - the gate-exit walk's own separate `exitStepMs` while exiting, otherwise the Speed Engine's current INTERPOLATED value (smoothly approaching its target, never snapping to it). */
  private currentInterpolatedStepMs(): number {
    return this.phase === "exiting" ? this.exitStepMs : this.speedEngine.currentStepMs;
  }

  private buildSpeedDebugSnapshot(): SpeedDebugSnapshot {
    const pressureAdjustedBaseMs = this.currentPressureAdjustedBaseMs();
    const growthBoost = this.currentGrowthBoost();
    const rushDebtBoost = this.currentRushDebtBoost();
    // Always 0 as of PR11.1 - SPEED TRIAL no longer contributes a
    // proportional boost here, see `currentFixedOverrideStepMs()` instead.
    const temporarySpeedBoost = 0;
    const uncappedTargetStepMs = computeUncappedTargetStepMs({
      pressureAdjustedBaseMs,
      growthBoost,
      rushDebtBoost,
      persistentModeBoost: this.persistentModeBoost,
      temporarySpeedBoost,
    });
    // Reuses `currentUnderlyingTargetStepMs()` itself (rather than
    // re-deriving `applySpeedCap(uncappedTargetStepMs, ...)` here again)
    // so the QA overlay's "underlying target" can never drift from the
    // real value movement actually uses - in particular, this is what
    // makes it correctly reflect Daily V6's pressure-curve combination
    // (`computeV6EffectiveStepMs()`) instead of silently showing the
    // pre-pressure-curve composed value for a V6 run.
    const underlyingTargetStepMs = this.currentUnderlyingTargetStepMs();
    const fixedOverride = this.currentFixedOverrideStepMs();
    const isFixedOverrideActive = fixedOverride !== null;
    const effectiveTargetStepMs = resolveEffectiveTargetStepMs(underlyingTargetStepMs, fixedOverride);
    const activeCapMs = this.activeSpeedCapMs();
    const currentStepMs = this.speedEngine.currentStepMs;
    const pct = (fraction: number) => Math.round(fraction * 1000) / 10;
    return {
      mode: this.speedMode,
      worldBaseStepMs: this.baseStepMs,
      pressureAdjustedBaseMs,
      actualLength: this.currentLength(),
      addedLength: Math.max(0, this.currentLength() - this.level.initialLength),
      growthBoostPercent: pct(growthBoost),
      cumulativeYellowCount: this.cumulativeYellowCount,
      rushDebtPercent: pct(rushDebtBoost),
      rushComboStack: this.rushComboStack,
      persistentModeBoostPercent: pct(this.persistentModeBoost),
      temporaryBoostPercent: pct(temporarySpeedBoost),
      activeCapMs,
      uncappedTargetStepMs,
      underlyingTargetStepMs,
      isFixedOverrideActive,
      fixedOverrideStepMs: SPEED_TRIAL_FIXED_STEP_MS,
      effectiveTargetStepMs,
      currentStepMs,
      currentCellsPerSecond: currentStepMs > 0 ? 1000 / currentStepMs : 0,
      isTransitioning: this.speedEngine.isTransitioning,
      transitionRemainingMs: this.speedEngine.transitionRemainingMs,
      speedTrialRemainingMs: this.speedTrial ? this.speedTrial.remainingMs : null,
      postCapRushStepMs: this.postCapRushStepMs,
    };
  }

  // ---------------------------------------------------------------------
  // Snapshot / accessors
  // ---------------------------------------------------------------------

  getSnapshot(): LevelGameSnapshot {
    // TRICK BITE/CLUTCH BITE points are flat, already-final integers -
    // included directly in Skill Score (and therefore Final Score), same
    // as every credited maneuver, but never routed back through
    // ManeuverTracker's own FLOW/speed/diminishing-returns math.
    const skillScore = this.maneuverTracker.skillScore + this.trickBiteTracker.bonusPoints;
    return {
      snake: this.snakeBody,
      bites: this.bites,
      direction: this.currentDirection,
      length: this.currentLength(),
      finalLength: this.finalLength,
      targetLength: this.targetLength,
      score: this.score,
      rushStack: this.rushComboStack,
      rushComboStreak: this.rushComboUncapped,
      cumulativeYellowCount: this.cumulativeYellowCount,
      speedBoostPercent: this.currentEffectiveBoostPercent(),
      isGateOpen: this.isGateOpenNow(),
      completionReason: this.completionReason,
      phase: this.phase,
      isGameOver: this.phase === "game-over",
      isLevelComplete: this.phase === "level-complete",
      isArenaCleared: this.phase === "arena-cleared",
      stepMs: this.currentInterpolatedStepMs(),
      targetStepMs: this.phase === "exiting" ? this.exitStepMs : this.speedEngine.targetStepMs,
      isSpeedTransitioning: this.phase === "exiting" ? false : this.speedEngine.isTransitioning,
      speedTransitionRemainingMs: this.phase === "exiting" ? 0 : this.speedEngine.transitionRemainingMs,
      speedDebug: this.buildSpeedDebugSnapshot(),
      speedTrialRemainingMs: this.speedTrial ? this.speedTrial.remainingMs : null,
      wormholeRemainingMs: this.wormhole ? this.wormhole.remainingMs : null,
      starfruitGateRemainingMs: this.starfruitGate ? this.starfruitGate.remainingMs : null,
      pendingRespawn: this.respawnTimerMs > 0 && !this.isGateOpenNow(),
      awaitingSpawnRetry: this.awaitingSpawnRetry,
      moveCount: this.moveCount,
      skillScore,
      finalScoreSoFar: this.score + skillScore,
      maneuverCounts: this.maneuverTracker.maneuverCounts,
      doubleDangerCount: this.maneuverTracker.doubleDangerCount,
      bestFlowStreak: this.maneuverTracker.bestFlowStreak,
      currentFlowStreak: this.maneuverTracker.currentFlowStreak,
      currentFlowMultiplier: this.maneuverTracker.currentFlowMultiplier,
      lastSecondTurnArmed: this.maneuverTracker.lastSecondTurnArmed,
      lastSecondTurnReArmProgress: this.maneuverTracker.lastSecondTurnReArmProgress,
      trickBiteCount: this.trickBiteTracker.trickBiteCount,
      clutchBiteCount: this.trickBiteTracker.clutchBiteCount,
      trickBiteWindowRemainingMoves: this.trickBiteTracker.windowRemainingMoves,
      trickBiteWindowOpenedBy: this.trickBiteTracker.windowOpenedByManeuvers,
      trickBiteWindowSource: this.trickBiteTracker.windowOpenedBySource,
    };
  }

  get gridCols(): number {
    return this.cols;
  }

  get gridRows(): number {
    return this.rows;
  }

  get obstacles(): readonly Point[] {
    return this.level.obstacles;
  }

  /** Portals V1 (Saturn World) - `[]` for every level without `portals`. For rendering's persistent portal markers only. */
  get portals(): readonly PortalDefinition[] {
    return this.level.portals ?? [];
  }

  /** BLACK HOLE V1 (Saturn World) - `[]` for every level without `blackHoles`. For rendering's persistent black-hole markers only. */
  get blackHoles(): readonly Point[] {
    return this.level.blackHoles ?? [];
  }

  get exitPosition(): Point {
    return this.exit;
  }
}

/**
 * The automatic gate-exit walk's own ANIMATION-speed formula -
 * deliberately NOT part of the gameplay Speed Engine
 * (`src/core/SpeedEngine.ts`) and never fed by it: no Growth boost, no
 * RUSH debt, no Daily pressure, no `persistentModeBoost` (HIGH SPEED),
 * no SPEED TRIAL. The exit walk is not player-controlled gameplay - it
 * is a fixed, uninterruptible animation whose only input is how many
 * segments are left to walk off the board, purely so a very long snake's
 * exit doesn't take forever and a very short one doesn't flash by
 * instantly. `clamp(totalDurationMs / length, minStepMs, maxStepMs)`.
 */
export function computeExitStepMs(length: number): number {
  const { totalDurationMs, minStepMs, maxStepMs } = GameConfig.gateExit;
  const raw = totalDurationMs / Math.max(1, length);
  return Math.max(minStepMs, Math.min(maxStepMs, raw));
}
